// Single-stroke lucide-style icons. Paths taken from videoroom.jsx, with a
// couple of extra ones (e.g. spinner). All SVGs share the same viewBox so
// `size` is a single number.

const PATHS: Record<string, string> = {
  mic: "M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3zM5 10v2a7 7 0 0 0 14 0v-2M12 19v4M8 23h8",
  micOff:
    "M3 3l18 18M9 9v3a3 3 0 0 0 5 2M12 1a3 3 0 0 1 3 3v6M5 10v2a7 7 0 0 0 10.6 6M9 19h6M12 19v4",
  cam: "M22 7l-7 5 7 5V7zM3 5h12v14H3z",
  camOff: "M3 3l18 18M22 7v10l-7-5M15 5H4a1 1 0 0 0-1 1v10",
  rec: "M12 4a8 8 0 1 1 0 16 8 8 0 0 1 0-16z",
  stop: "M6 6h12v12H6z",
  end: "M21 12a9 9 0 0 0-18 0M5 12l-2 2M19 12l2 2M9 14h6",
  chat: "M21 11.5a8.4 8.4 0 0 1-9.5 8.3L3 21l1.2-4.8A8.5 8.5 0 1 1 21 11.5z",
  more: "M5 12h.01M12 12h.01M19 12h.01",
  link: "M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1",
  person: "M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM2 21c0-3.3 2.7-6 6-6s6 2.7 6 6",
  flip: "M16 3h5v5M4 20l16.7-16.7M21 16v5h-5M15 15l5 5",
  info: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 8h.01M12 12v5",
  check: "M5 12l5 5L20 7",
  x: "M6 6l12 12M18 6l-12 12",
  share: "M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7M16 6l-4-4-4 4M12 2v13",
  spinner: "M12 2a10 10 0 0 1 10 10",
  // Background blur indicator — concentric arcs hinting at depth-of-field.
  blur:
    "M12 4a8 8 0 1 1 0 16 8 8 0 0 1 0-16zM12 8a4 4 0 0 0-4 4M12 16a4 4 0 0 0 4-4",
  // Portrait light — sun: disc + rays.
  sun:
    "M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4",
  // Camera flip — two arrows curving around a circle (front/back swap).
  camFlip:
    "M3 12a9 9 0 0 1 14.5-7L21 7M21 12a9 9 0 0 1-14.5 7L3 17M21 3v4h-4M3 21v-4h4",
};

export function Icon({
  name,
  size = 18,
  stroke = 1.8,
  spin = false,
}: {
  name: keyof typeof PATHS | string;
  size?: number;
  stroke?: number;
  spin?: boolean;
}) {
  const d = PATHS[name];
  if (!d) return null;
  const segments = d.split(/(?=M)/);
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={
        spin
          ? { animation: "iconSpin 0.9s linear infinite" }
          : undefined
      }
    >
      {segments.map((p, i) => (
        <path key={i} d={p} />
      ))}
    </svg>
  );
}

export const ICON_KEYFRAMES = `
@keyframes iconSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
`;
